import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, AlertCircle, Info, CheckCircle } from "lucide-react";
import { cn } from "../../utils/cn";
import Button from "./Button";

export type ConfirmationVariant =
  | "default"
  | "warning"
  | "error"
  | "info"
  | "success";

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  variant?: ConfirmationVariant;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  variant = "default",
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const variants = {
    default: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" },
    warning: {
      icon: AlertTriangle,
      color: "text-yellow-500",
      bg: "bg-yellow-50",
    },
    error: { icon: AlertCircle, color: "text-red-500", bg: "bg-red-50" },
    info: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" },
    success: { icon: CheckCircle, color: "text-green-500", bg: "bg-green-50" },
  };

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false)
    }
  }, [isOpen])

  const Icon = variants[variant].icon;
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="backdrop"
        className="fixed inset-0 z-[150] bg-black bg-opacity-50 flex items-center justify-center ra"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          key="dialog"
          className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg z-50 overflow-hidden"
          initial={{ opacity: 0, scale: 0.95, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 30 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className={cn(
              "flex items-start gap-3 p-5 border-b",
              variants[variant].bg,
            )}
          >
            <Icon className={cn("mt-1", variants[variant].color)} size={24} />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <p className="text-sm text-gray-600 mt-1">{message}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-5 bg-gray-50 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              {cancelText}
            </Button>
            <Button
              className="bg-primary-button hover:bg-primary-button"
              variant="primary"
              onClick={async () => {
                setIsLoading(true);
                await onConfirm();
                setIsLoading(false)
              }}
              loading={isLoading || loading}
              disabled={isLoading || loading}
            >
              {confirmText}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

export default ConfirmationDialog;
